import { FocusMonitor } from '@angular/cdk/a11y'
import { Component, ElementRef, Input, OnDestroy, OnInit, Optional, Self, ViewChild } from '@angular/core'
import { FormGroupDirective, NgControl, NgForm } from '@angular/forms'
import { DateAdapter } from '@angular/material/core'
import { MatDatepickerInputEvent } from '@angular/material/datepicker'
import { MatFormFieldControl } from '@angular/material/form-field'
import { MaskitoOptions } from '@maskito/core'
import { I18nService } from '@mmx/shared'
import moment from 'moment'
import { filter, Subscription } from 'rxjs'

import { CustomMaterialInputControlComponent } from '../custom-field'

type DateType = moment.Moment

@Component({
  selector: 'mmx-date-selector-input',
  templateUrl: './date-selector.component.html',
  styleUrls: ['./date-selector.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: DateSelectorComponent,
    },
  ],
  standalone: false,
})
export class DateSelectorComponent extends CustomMaterialInputControlComponent<DateType> implements OnInit, OnDestroy {
  @Input() startView = 'month'
  @Input() max: DateType | string
  @Input() min: DateType
  @Input() dob = false

  @ViewChild('textInput') input: HTMLInputElement

  dateMask: MaskitoOptions = {
    mask: [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/],
  }

  private statusSubscription: Subscription

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    @Optional() protected parentForm: NgForm,
    @Optional() protected parentFormGroup: FormGroupDirective,
    protected fm: FocusMonitor,
    protected elRef: ElementRef,
    protected dateAdapter: DateAdapter<any>,
    protected i18n: I18nService,
  ) {
    super(ngControl, parentForm, parentFormGroup, fm, elRef)
    this.i18n.localeSubject
      .pipe(
        filter(Boolean),
      )
      .subscribe((lang) => {
        // Change language of Datepicker
        this.dateAdapter.setLocale(lang)
      })
  }

  ngOnInit() {
    super.ngOnInit()

    if (this.dob) {
      this.max = moment().format('YYYY-MM-DD')
      this.startView = 'multi-year'
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy()

    if (this.statusSubscription) {
      this.statusSubscription.unsubscribe()
    }
  }

  get dateValue(): string {
    if (this.value) {
      const mom = moment(this.value)
      if (mom.isValid()) {
        return mom.format('MM/DD/YYYY')
      }
    }
  }

  set dateValue(value: string) {}

  get datePickerValue(): DateType {
    return this.value
  }

  set datePickerValue(value: DateType) {}

  onTextInputChange($event: Event, textInput: HTMLInputElement) {
    const dateString = textInput.value

    if (dateString) {
      const date = moment(dateString, 'MM/DD/YYYY', true)

      if (date.isValid() && !date.isSame(this.value, 'day')) {
        this.value = date
      }
    } else {
      this.value = null
    }
  }

  onDatePickerChange(type: string, event: MatDatepickerInputEvent<DateType>) {
    this.value = event.value

    if (this.value) {
      // we need to manually set the input of the text-mask
      const textInputEl = this.elRef.nativeElement.querySelector('.date-input')

      if (textInputEl) {
        textInputEl.value = moment(this.value).format('MM/DD/YYYY')
      }
    }
  }

  onContainerClick(event: MouseEvent) {
    super.onContainerClick(event)

    this.fm.focusVia(this.input, 'program')
  }
}
